// ORDENAR POR EL NOMBRE DEL CLIENTE, DE LA A A LA Z — Y COMO LO TENGA PUESTO
// LA AGENCIA.
//
// Lissette, 2026-09-15: *"en póliza colocar filtro de A a la Z"*, y al
// preguntarle por cuál columna: *"clientes"*. Y acto seguido, lo que faltaba:
// *"en el sistema tenemos que vean por nombre y apellido, y apellido y nombre,
// tiene que ser según la configuración de la agencia; por eso tiene que cuadrar
// así la A-Z o Z-A, según nombre o apellido como lo tenga la agencia
// configurado"*.
//
// ── LA IDEA, Y ES UNA SOLA LÍNEA ───────────────────────────────────────────
//
// Se ordena por **el mismo texto que se pinta**: `formatClientName(c)`, que ya
// conoce la preferencia (`first-last` → "Nombre Segundo Apellido";
// `last-first` → "Apellido, Nombre Segundo").
//
// Con eso, el orden cuadra solo y sin una tabla de casos aparte:
//   - configurada en NOMBRE  → "Ana Pérez" ordena por Ana.
//   - configurada en APELLIDO → "Pérez, Ana" ordena por Pérez.
//
// La alternativa —una regla propia que decida "primero apellido, luego
// nombre"— es una SEGUNDA definición de lo mismo, y el día que la preferencia
// gane una opción (con segundo apellido, sin segundo nombre…) una de las dos se
// entera y la otra no. Aquí no hay dos: hay la función que pinta.
//
// Módulo PLANO —sin React— para poder probarlo con listas de verdad desde
// vitest (regla de oro 48: un .jsx no se importa desde una prueba del backend).
// `utils.js` y `ordenDeTabla.js` no importan nada, así que no hay ciclo.
import { compararValores } from './ordenDeTabla.js';
import { formatClientName, getNameOrderPref } from './utils.js';

/**
 * El nombre de una persona tal y como se pinta, honrando la preferencia.
 *
 * `orden` se pasa solo en las pruebas; en la aplicación sale del singleton que
 * se siembra al entrar (`setNameOrderPref`), igual que el formato de fecha.
 */
export function nombreParaOrdenar(persona, orden) {
  return formatClientName(persona, orden || getNameOrderPref()).trim();
}

/**
 * El nombre del cliente de una póliza, tal y como lo pinta su fila.
 *
 * `buscarCliente` la pone la pantalla (es quien tiene la lista de fichas). Sin
 * ficha se cae al `clientName` que trae la propia póliza —las importadas lo
 * traen— y si tampoco, cadena vacía: "sin dato", no "AAA".
 */
export function nombreDelClienteDePoliza(poliza, buscarCliente, orden) {
  const c = (typeof buscarCliente === 'function' && poliza)
    ? buscarCliente(poliza.clientId)
    : null;
  const n = c ? nombreParaOrdenar(c, orden) : '';
  return n || String((poliza && poliza.clientName) || '').trim();
}

/**
 * Compara DOS PERSONAS por su nombre, en el orden que tenga puesto la agencia.
 *
 * Lo usan la lista de Clientes y el orden de Pólizas, para que las dos
 * contesten igual. `compararValores` es el mismo comparador de Pagos del Mes y
 * de Tareas: lo vacío al final —suba o baje el orden—, acentos y mayúsculas sin
 * distinguir.
 */
export function compararNombresDeCliente(a, b, direccion = 'asc', orden, es = true) {
  return compararValores(
    nombreParaOrdenar(a, orden).toLowerCase(),
    nombreParaOrdenar(b, orden).toLowerCase(),
    direccion,
    es,
  );
}

/**
 * Las pólizas ordenadas por el nombre del cliente. No toca el array que llega.
 *
 * LO QUE NO TIENE NOMBRE SE VA AL FINAL en las dos direcciones — lo mismo que
 * hace `ordenarPorAnio` con las pólizas sin año. Una lista que empieza con
 * cinco renglones vacíos no sirve, y en 'desc' colarlos primero sería peor.
 *
 * El desempate es la ASEGURADORA y luego el plan: un cliente con seis pólizas
 * las vería bailar entre refrescos si el empate quedara al azar del navegador
 * (`Array.sort` no promete estabilidad entre motores para listas grandes).
 */
export function ordenarPorCliente(polizas, direccion = 'asc', buscarCliente, es = true, orden) {
  const lista = Array.isArray(polizas) ? [...polizas] : [];
  const nombre = (p) => nombreDelClienteDePoliza(p, buscarCliente, orden).toLowerCase();
  const idioma = es ? 'es' : 'en';
  return lista.sort((a, b) => {
    const r = compararValores(nombre(a), nombre(b), direccion, es);
    if (r !== 0) return r;
    const porCarrier = String((a && a.carrier) || '').localeCompare(String((b && b.carrier) || ''), idioma);
    if (porCarrier !== 0) return porCarrier;
    return String((a && a.plan) || '').localeCompare(String((b && b.plan) || ''), idioma);
  });
}
